import * as Icons from "@mui/icons-material";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import Box from "@mui/material/Box";
import ButtonBase from "@mui/material/ButtonBase";
import Tooltip from "@mui/material/Tooltip";
import React, { useContext, useState } from "react";

import AvatarWithStatus from "@/components/AvatarWithStatus";
import { zinc } from "@/components/DriverView/ui/colors";
import FeedCard from "@/components/DriverView/ui/FeedCard";
import FeedItemMenu from "@/components/feed/driver-centric/FeedItemMenu";
import FeedItemModals from "@/components/feed/shared/FeedItemModals";
import FeedItemStatus from "@/components/feed/shared/FeedItemStatus";
import { useFeedItemData } from "@/components/feed/shared/useFeedItemData";
import type { Feed } from "@/db/types";
import { useFeedItemCardVariant } from "@/hooks/driver/useFeedItemCardVariant";
import { AudioAppContext } from "@/models/AudioAppContextProvider";
import type { FullItem } from "@/models/FeedContextProvider";
import DriverCentricFeedItemContent from "./DriverCentricFeedItemContent";

type Props = {
  item: FullItem;
  feed: Feed;
  vListIndex?: number;
  active?: boolean;
  highlightPodcast: boolean;
  setItemNotLoaded?: (value: string) => void;
  preventScroll?: (value: boolean) => void;
  aliasChannelOwnerId?: string;
};

export default function DriverCentricFeedItem({
  item,
  feed,
  active,
  highlightPodcast,
  preventScroll,
  aliasChannelOwnerId,
}: Props) {
  const data = useFeedItemData({ item, feed, active, highlightPodcast, preventScroll, aliasChannelOwnerId });
  const { activeItemId } = useContext(AudioAppContext);
  const [statusModalOpen, setStatusModalOpen] = useState<boolean>(false);

  const activePlayItem = activeItemId === item.id;
  const isFromDriver = item?.account?.role === "limitedMember";
  const variant = useFeedItemCardVariant({ mine: data.mine, isFromDriver });

  if (!item || !data.shouldRender) return null;

  return (
    <Box
      ref={data.ref}
      id={item.id}
      sx={(theme) => ({
        display: "flex",
        justifyContent: data.mine ? "flex-end" : "flex-start",
        width: "100%",
        ...(active && { bgcolor: `${theme.palette.altBrand[400]}66` }),
      })}
    >
      <FeedItemModals {...data.feedItemModalsProps} />
      <Box
        id={`feed-item-${item.id}`}
        sx={{
          pt: 1,
          pb: 1,
          width: "75%",
          minWidth: "350px",
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, fontSize: "0.875rem", color: zinc[500], mb: 1 }}>
          <Box>
            <AvatarWithStatus size={20} accountId={item.account?.id} key={item.account?.id} />
          </Box>
          <Box>{item.account?.name}</Box>
          <Box>•</Box>
          <Box>{data.itemDate}</Box>
          {item?.isSilent && (
            <Tooltip title="Silent Message">
              <Icons.VolumeOffOutlined
                fontSize="small"
                sx={{
                  color: "primary.main",
                  bgcolor: "error.main",
                  borderRadius: 1,
                }}
              />
            </Tooltip>
          )}
        </Box>
        <FeedCard variant={variant} playing={activePlayItem} hasArtifact={!!item.displayArtifact}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
            <DriverCentricFeedItemContent
              mine={data.mine}
              feedItemId={data.feedItemId}
              shouldShowAudioTranscriptContent={data.shouldShowAudioTranscriptContent}
              hasCallRecord={data.hasCallRecord}
              filesOrLinks={data.filesOrLinks}
              duration={data.duration}
              itemId={item.id}
              hasPlayableAudio={data.hasPlayableAudio}
              onPlay={data.handlePlay}
              onFinished={data.handleFinished}
              onPause={data.handlePause}
              displayArtifactRecord={data.displayArtifactRecord}
              showModalTranscription={data.showModalTranscription}
              setShowModalTranscription={data.setShowModalTranscription}
              feedId={item.feedId}
              showTranscription={data.showTranscription}
              preferredLanguage={data.preferredLanguage}
              transcriptions={data.transcriptions}
              transcriptionContent={data.transcriptionContent}
              unreliableTranscription={data.unreliableTranscription}
              unreliableTranscriptLabel={data.unreliableTranscriptLabel}
              transcriptTranslatedFrom={data.transcriptTranslatedFrom}
              hasTranscription={data.hasTranscription}
              files={data.files}
              links={data.links}
              hasDisplayArtifact={(value: string) => data.setDisplayArtifact(() => value)}
              callRecords={data.callRecords}
            />
          </Box>
        </FeedCard>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, mt: 1 }}>
          <Box sx={{ flexGrow: 1, display: "flex", gap: 1, alignItems: "center" }}>
            {data.flags.debugMode && (
              <ButtonBase
                onClick={() => data.handleSetDebuggerOpen(true)}
                sx={{
                  bgcolor: zinc[800],
                  color: zinc[600],
                  "&:hover": { color: "white", bgcolor: zinc[700] },
                  p: 0.25,
                  fontSize: "0.75rem",
                  cursor: "pointer",
                  borderRadius: 0.5,
                  lineHeight: 1,
                }}
              >
                <Icons.InfoOutlined role="img" />
              </ButtonBase>
            )}
            <Box sx={{ display: "flex", alignItems: "center" }}>
              <FeedItemStatus
                itemId={item.id}
                sender={data.senderAccount}
                allDriverEvents={data.allDriverEvents}
                count={data.count}
                groups={data.groups}
                createdAt={item.createdAt}
                openModal={statusModalOpen}
                setOpenModalStatus={() => setStatusModalOpen(() => false)}
              />
            </Box>
          </Box>
          <Box sx={{ display: "flex", alignItems: "center" }}>
            <FeedItemMenu
              workspaceId={feed.workspaceId}
              item={item}
              MenuOptions={data.MenuOptions}
              displayArtifact={data.displayArtifact}
              setConfirmDeleteDialog={data.handleSetConfirmDeleteDialog}
              setStatusModalOpen={(value) => setStatusModalOpen(value)}
              textTranscript={data.textTranscript}
              buttonElement={
                <Box
                  sx={{
                    cursor: "pointer",
                    bgcolor: zinc[800],
                    color: zinc[400],
                    "&:hover": { bgcolor: zinc[700], color: "white" },
                    borderRadius: 0.5,
                    display: "flex",
                    alignItems: "center",
                  }}
                >
                  <MoreVertIcon role="img" />
                </Box>
              }
            />
          </Box>
        </Box>
      </Box>
    </Box>
  );
}
